import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { can, useAuth } from '../lib/auth';
import { useVendors } from '../lib/lookups';
import { isCutSku } from '../lib/cutLeftover';
import {
  BOARD_PO_DEFAULT_DELIVERY, BOARD_PO_DEFAULT_TERMS, BOARD_PO_UOMS,
  amountInWords, lineAmounts, money, poDateText, printBoardPo, qtyText, sheetKg,
  type BoardPoDoc,
} from '../lib/boardPo';
import { vendorFormatFor, vendorFormatForName, type VendorPoFormat } from '../lib/vendorPoFormats';

/**
 * Purchase orders on the company PO form — the Purchase Order tab of the
 * Paper Stock dashboard (boards) and of the Raw Material and Spare Parts
 * inventory pages.  Raise a PO to a supplier (Vendor Master), print it, and
 * see what has arrived against it.  All kinds share one PO number series.
 *
 * Deliveries are booked with "Receive" on the board / item (or the Receive
 * button on a PO line, which opens the same form with the PO filled in); the
 * API adds up those receipts by PO number, so this screen never keeps its own
 * received count.
 */

export type PoKind = 'BOARD' | 'RAW_MATERIAL' | 'SPARE_PARTS';

export interface BoardRef {
  id: number;
  sku: string;
  paperType: string;
  lengthIn: number | null;
  widthIn: number | null;
  gsm: number | null;
  brand: string;
  rate: number | null;
  sheetsOnHand: number;
}

/** A raw material / spare parts item, as a PO line and the Receive form see it. */
export interface StoreItemRef {
  code: string;
  /** The SKU the store screens show (SKU1…); the code until it is numbered. */
  sku?: string | null;
  name: string;
  /** The item's group and maker, as the stock table shows them. */
  group?: string | null;
  brand?: string | null;
  uom: string | null;
  category: string;
  standardCost: number | null;
  qtyOnHand: number;
  location: string | null;
}

/** What the Receive form is opened with when booking against a PO line. */
export interface ReceivePreset {
  poNumber: string;
  qty?: number;
  /** Per sheet for a board line bought in sheets; per the item's unit for an inventory item. */
  rate?: number | null;
  /** Per kg — a board line bought by weight (kg, or tonnes / 1000). */
  ratePerKg?: number | null;
}

type PoStatus = 'OPEN' | 'PARTIAL' | 'RECEIVED' | 'CANCELLED' | 'CLOSED';

interface PoLine {
  id: number;
  lineNo: number;
  paperStockId: number | null;
  itemCode: string | null;
  description: string;
  qty: number;
  uom: string;
  rate: number;
  gstPercent: number;
  /** A supplier form's own columns (Century: reelSheet, grain, discount, remarks). */
  formFields?: Record<string, string>;
  amount: number;
  gstAmount: number;
  total: number;
  /** Board lines: sheets received. */
  receivedSheets: number;
  /** Item lines: received, in the item's unit. */
  receivedQty: number;
  board: BoardRef | null;
  item: StoreItemRef | null;
}

interface Po {
  id: number;
  kind: PoKind;
  poNumber: string;
  poDate: string;
  vendorId: string;
  vendorName: string;
  supplierAddress: string;
  deliveryLocation: string;
  terms: string;
  remarks: string;
  storedStatus: string;
  status: PoStatus;
  receivedSheets: number;
  subTotal: number;
  gstTotal: number;
  grandTotal: number;
  createdOn: string;
  createdByName: string;
  lines: PoLine[];
}

/** Raw material / spare parts: which item categories and units belong on the PO. */
interface StoreScope { categories: string[]; uoms: string[] }

const KIND_TEXT: Record<PoKind, { pos: string; title: string }> = {
  BOARD:        { pos: 'board POs',        title: 'board purchase order' },
  RAW_MATERIAL: { pos: 'raw material POs', title: 'raw material purchase order' },
  SPARE_PARTS:  { pos: 'spare parts POs',  title: 'spare parts purchase order' },
};

const STATUS_META: Record<PoStatus, { label: string; cls: string }> = {
  OPEN:      { label: 'Open',            cls: 'bg-sky-100 text-sky-800' },
  PARTIAL:   { label: 'Partly received', cls: 'bg-amber-100 text-amber-800' },
  RECEIVED:  { label: 'Received',        cls: 'bg-emerald-100 text-emerald-800' },
  CLOSED:    { label: 'Closed',          cls: 'bg-slate-200 text-slate-700' },
  CANCELLED: { label: 'Cancelled',       cls: 'bg-red-100 text-red-700' },
};

function StatusBadge({ status }: { status: PoStatus }) {
  const m = STATUS_META[status];
  return <span className={`px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

/** "GREY BACK, 26.5 x 38.5 inch, 340 GSM, ITC" — suppliers work in inches. */
export function boardSpec(b: Pick<BoardRef, 'paperType' | 'lengthIn' | 'widthIn' | 'gsm' | 'brand'>): string {
  const size = b.lengthIn && b.widthIn ? `${b.lengthIn} x ${b.widthIn} inch` : '';
  return [b.paperType, size, b.gsm ? `${b.gsm} GSM` : '', b.brand].filter(Boolean).join(', ');
}

/**
 * The board's rate — rupees per kg — in a PO line's unit: as is per KG, x1000
 * per TON, x kg-per-sheet per SHEET.  Null for PKT / NOS or when the board has
 * no rate (or, per sheet, no size and GSM).
 */
function boardRateIn(uom: string, b: BoardRef | null): number | null {
  if (!b?.rate || !(b.rate > 0)) return null;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  if (uom === 'KG') return r2(b.rate);
  if (uom === 'TON') return r2(b.rate * 1000);
  if (uom === 'SHEETS') { const kg = sheetKg(b); return kg > 0 ? r2(b.rate * kg) : null; }
  return null;
}

/** The other unit, so purchase can sanity-check an order: sheets ↔ kg. */
function weightHint(qty: number, uom: string, b: BoardRef | null): string {
  const per = sheetKg(b);
  if (!per || !(qty > 0)) return '';
  if (uom === 'SHEETS') return `≈ ${qtyText(Math.round(qty * per))} kg`;
  if (uom === 'KG') return `≈ ${qtyText(Math.round(qty / per))} sheets`;
  if (uom === 'TON') return `≈ ${qtyText(Math.round((qty * 1000) / per))} sheets`;
  return '';
}

/** The line is in the item's own unit, so what arrives can be set against what was ordered. */
const inItemUnit = (l: Pick<PoLine, 'uom' | 'item'>) =>
  !l.item?.uom || l.item.uom.trim().toUpperCase() === l.uom.trim().toUpperCase();

function poError(m: string): string {
  switch (m) {
    case 'po_number_taken':  return 'That PO number is already used — leave it blank to take the next free number.';
    case 'duplicate_board':  return 'The same board is on two lines — put it on one line.';
    case 'duplicate_item':   return 'The same item is on two lines — put it on one line.';
    case 'board_not_found':  return 'A board on this PO no longer exists — pick it again.';
    case 'item_not_found':   return 'An item on this PO is no longer in the item master — pick it again.';
    case 'vendor_not_found': return 'That supplier is no longer in Vendor Master — pick another.';
    case 'po_not_open':      return 'This PO is cancelled or closed — reopen it first.';
    case 'po_has_receipts':  return 'Stock has already been received against this PO, so it can no longer be edited.';
    case 'po_kinds_need_migration':
      return 'The database has not been updated for raw material / spare parts POs yet (migration_2026_09_29_po_kinds).';
    case 'validation_failed': return 'Some fields are missing or invalid — check the supplier, the date and every line.';
    default: return m;
  }
}

const toDoc = (po: Po): BoardPoDoc => ({
  kind: po.kind,
  poNumber: po.poNumber,
  poDate: po.poDate,
  vendorName: po.vendorName,
  supplierAddress: po.supplierAddress,
  deliveryLocation: po.deliveryLocation,
  terms: po.terms,
  remarks: po.remarks,
  lines: po.lines.map((l) => ({
    description: l.description, qty: l.qty, uom: l.uom, rate: l.rate, gstPercent: l.gstPercent,
    board: l.board ? { paperType: l.board.paperType, lengthIn: l.board.lengthIn, widthIn: l.board.widthIn, gsm: l.board.gsm, brand: l.board.brand } : null,
    formFields: l.formFields ?? {},
  })),
});

/** Today in the browser's timezone (India), as yyyy-mm-dd. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Sum of quantities per unit: "5,000 SHEETS · 1,200 KG". */
function qtySummary(lines: PoLine[]): string {
  const byUom = new Map<string, number>();
  for (const l of lines) byUom.set(l.uom, (byUom.get(l.uom) ?? 0) + l.qty);
  return [...byUom].map(([u, n]) => `${qtyText(n)} ${u}`).join(' · ');
}

/** What is in vs ordered, over the lines tied to a board / item. */
function receivedSummary(po: Po): string {
  if (po.kind === 'BOARD') {
    const tracked = po.lines.filter((l) => l.board);
    if (!tracked.length) return '—';
    const got = tracked.reduce((s, l) => s + l.receivedSheets, 0);
    const sheetLines = tracked.filter((l) => l.uom === 'SHEETS');
    if (sheetLines.length === tracked.length) {
      return `${qtyText(got)} / ${qtyText(sheetLines.reduce((s, l) => s + l.qty, 0))} sheets`;
    }
    return got ? `${qtyText(got)} sheets` : '—';
  }
  const tracked = po.lines.filter((l) => l.item);
  if (!tracked.length) return '—';
  const units = new Set(tracked.map((l) => l.uom));
  if (units.size === 1) {
    const got = tracked.reduce((s, l) => s + l.receivedQty, 0);
    return `${qtyText(got)} / ${qtyText(tracked.reduce((s, l) => s + l.qty, 0))} ${[...units][0]}`;
  }
  const done = tracked.filter((l) => inItemUnit(l) && l.receivedQty >= l.qty).length;
  return `${done} / ${tracked.length} lines in`;
}

function usePoInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['board-pos'] });
    qc.invalidateQueries({ queryKey: ['board-po'] });
    qc.invalidateQueries({ queryKey: ['board-po-open-for'] });
    qc.invalidateQueries({ queryKey: ['board-po-open-for-item'] });
    qc.invalidateQueries({ queryKey: ['board-po-next'] });
  };
}

/* ─────────────────────────── the tab ─────────────────────────── */

/** Board POs — the Purchase Order tab of the Paper Stock dashboard. */
export function BoardPoTab({ onReceive }: { onReceive: (board: BoardRef, preset: ReceivePreset) => void }) {
  return <PoTab kind="BOARD" onReceiveBoard={onReceive} />;
}

/** Raw material or spare parts POs — the Purchase Orders tab of that inventory page. */
export function StorePoTab({ kind, categories, uoms, onReceive }: {
  kind: 'RAW_MATERIAL' | 'SPARE_PARTS';
  /** Item categories that belong on this kind of PO. */
  categories: string[];
  /** Units offered on a line not tied to an item. */
  uoms: string[];
  onReceive: (item: StoreItemRef, preset: ReceivePreset) => void;
}) {
  const scope = useMemo(() => ({ categories, uoms }), [categories, uoms]);
  return <PoTab kind={kind} scope={scope} onReceiveItem={onReceive} />;
}

function PoTab({ kind, scope, onReceiveBoard, onReceiveItem }: {
  kind: PoKind;
  scope?: StoreScope;
  onReceiveBoard?: (board: BoardRef, preset: ReceivePreset) => void;
  onReceiveItem?: (item: StoreItemRef, preset: ReceivePreset) => void;
}) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | 'PENDING' | PoStatus>('');
  const [editing, setEditing] = useState<Po | 'new' | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const { user } = useAuth();
  // Accounts and GM see the POs and print them; raising and changing them is Purchase's.
  const canManage = can(user, 'MANAGE_INVENTORY');

  const list = useQuery({
    queryKey: ['board-pos', kind, q.trim()],
    queryFn: () => api<{ rows: Po[]; needsMigration?: boolean }>(
      `/api/board-po?kind=${kind}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`),
    placeholderData: (prev) => prev,
  });

  const rows = (list.data?.rows ?? []).filter((r) =>
    !status ? true
      : status === 'PENDING' ? r.status === 'OPEN' || r.status === 'PARTIAL'
      : r.status === status);
  const needsMigration = !!list.data?.needsMigration;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {canManage && <button className="btn-primary" onClick={() => setEditing('new')} disabled={needsMigration}>+ New PO</button>}
        <input
          className="input flex-1 min-w-[220px] max-w-md"
          placeholder="Search PO no., supplier or item"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="">All POs</option>
          <option value="PENDING">Pending (open + partly received)</option>
          <option value="OPEN">Open</option>
          <option value="PARTIAL">Partly received</option>
          <option value="RECEIVED">Received</option>
          <option value="CLOSED">Closed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <div className="text-xs text-slate-500 ml-auto">{rows.length} PO(s)</div>
      </div>

      {needsMigration && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {poError('po_kinds_need_migration')}
        </div>
      )}

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-slate-600">PO No.</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Supplier</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Items</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Qty</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Total (incl. GST)</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Received</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading ? (
              <tr><td colSpan={8} className="p-6 text-center text-slate-400">Loading…</td></tr>
            ) : list.isError ? (
              <tr><td colSpan={8} className="p-6 text-center text-red-600">Could not load POs: {(list.error as Error).message}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-slate-400">
                {list.data?.rows.length ? 'No PO matches this filter.'
                  : `No ${KIND_TEXT[kind].pos} yet${canManage ? ' — click “+ New PO”' : ''}.`}
              </td></tr>
            ) : rows.map((po) => (
              <tr key={po.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewing(po.id)}>
                <td className="px-3 py-1.5 font-mono font-semibold text-brand whitespace-nowrap">{po.poNumber}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{poDateText(po.poDate)}</td>
                <td className="px-3 py-1.5 max-w-[200px] truncate" title={po.vendorName}>{po.vendorName}</td>
                <td className="px-3 py-1.5 max-w-[280px]">
                  <div className="truncate" title={po.lines.map((l) => l.description).join('\n')}>{po.lines[0]?.description ?? '—'}</div>
                  {po.lines.length > 1 && <div className="text-[10px] text-slate-400">+{po.lines.length - 1} more</div>}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">{qtySummary(po.lines)}</td>
                <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap">₹{money(po.grandTotal)}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">{receivedSummary(po)}</td>
                <td className="px-3 py-1.5"><StatusBadge status={po.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PoEditor
          kind={kind}
          scope={scope}
          po={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(id) => { setEditing(null); setViewing(id); }}
        />
      )}
      {viewing != null && !editing && (
        <PoViewer
          id={viewing}
          onClose={() => setViewing(null)}
          onEdit={(po) => setEditing(po)}
          onReceiveBoard={onReceiveBoard}
          onReceiveItem={onReceiveItem}
        />
      )}
    </>
  );
}

/* ─────────────────────────── viewer ─────────────────────────── */

function PoViewer({ id, onClose, onEdit, onReceiveBoard, onReceiveItem }: {
  id: number;
  onClose: () => void;
  onEdit: (po: Po) => void;
  onReceiveBoard?: (board: BoardRef, preset: ReceivePreset) => void;
  onReceiveItem?: (item: StoreItemRef, preset: ReceivePreset) => void;
}) {
  const invalidate = usePoInvalidate();
  const { data: po, isLoading, error } = useQuery({
    queryKey: ['board-po', id],
    queryFn: () => api<Po>(`/api/board-po/${id}`),
  });
  const [err, setErr] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const { user } = useAuth();
  const canManage = can(user, 'MANAGE_INVENTORY');

  const closePo = useMutation({
    mutationFn: () => api<{ status: PoStatus }>(`/api/board-po/${id}/close`, { method: 'POST' }),
    onSuccess: invalidate,
    onError: (e) => setErr(poError((e as Error).message)),
  });
  const reopenPo = useMutation({
    mutationFn: () => api(`/api/board-po/${id}/reopen`, { method: 'POST' }),
    onSuccess: invalidate,
    onError: (e) => setErr(poError((e as Error).message)),
  });

  // The supplier's own order form, when it has one (Century ...).
  const vendorForm = po ? vendorFormatFor(toDoc(po)) : undefined;

  async function downloadPdf(form?: VendorPoFormat) {
    if (!po) return;
    setErr(null);
    setPdfBusy(true);
    try {
      const { generateBoardPoPdf } = await import('../lib/generateBoardPoPdf');
      await generateBoardPoPdf(toDoc(po), form);
    } catch (e) {
      setErr(`Could not build the PDF: ${(e as Error).message}`);
    } finally {
      setPdfBusy(false);
    }
  }

  function print(form?: VendorPoFormat) {
    if (!po) return;
    setErr(null);
    try { printBoardPo(toDoc(po), form); } catch (e) { setErr((e as Error).message); }
  }

  const isOpen = po?.storedStatus === 'OPEN';
  const canEdit = canManage && isOpen && po?.status === 'OPEN';

  /** The Receive button's line: what is still due and what to open the form with. */
  function receiveFor(p: Po, l: PoLine): (() => void) | null {
    if (!isOpen || !canManage) return null;
    if (l.board && onReceiveBoard) {
      const pending = l.uom === 'SHEETS' ? Math.max(0, l.qty - l.receivedSheets) : null;
      if (pending === 0) return null;
      const board = l.board;
      return () => onReceiveBoard(board, {
        poNumber: p.poNumber,
        qty: pending ?? undefined,
        // A per-sheet rate only makes sense for a line bought in sheets.
        rate: l.uom === 'SHEETS' && l.rate > 0 ? l.rate : undefined,
        ratePerKg: l.rate > 0 ? (l.uom === 'KG' ? l.rate : l.uom === 'TON' ? l.rate / 1000 : undefined) : undefined,
      });
    }
    if (l.item && onReceiveItem) {
      const same = inItemUnit(l);
      const pending = same ? Math.max(0, l.qty - l.receivedQty) : null;
      if (pending === 0) return null;
      const item = l.item;
      return () => onReceiveItem(item, {
        poNumber: p.poNumber,
        qty: pending ?? undefined,
        rate: same && l.rate > 0 ? l.rate : undefined,
      });
    }
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-5xl w-full mx-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {isLoading || !po ? (
          <div className="p-6 text-center text-slate-500">
            {error ? `Could not load the PO: ${(error as Error).message}` : 'Loading…'}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-brand font-mono">{po.poNumber}</h2>
                  <StatusBadge status={po.status} />
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {poDateText(po.poDate)}
                  {po.createdByName && <> · raised by {po.createdByName}</>}
                </div>
              </div>
              <button className="btn-ghost text-sm" onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4 text-xs">
              <div className="md:col-span-2 rounded border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Supplier</div>
                <div className="font-semibold text-sm">{po.vendorName}</div>
                <div className="whitespace-pre-line text-slate-600">{po.supplierAddress || <span className="text-amber-700">No address in Vendor Master yet.</span>}</div>
              </div>
              <div className="rounded border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Delivery location</div>
                <div className="font-semibold">{po.deliveryLocation || BOARD_PO_DEFAULT_DELIVERY}</div>
                {po.remarks && (
                  <>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-2 mb-1">Remarks</div>
                    <div className="whitespace-pre-line">{po.remarks}</div>
                  </>
                )}
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">#</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Description</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Qty</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Rate</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Amount</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">GST</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Total</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Received</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {po.lines.map((l) => {
                    const receive = receiveFor(po, l);
                    return (
                      <tr key={l.id} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 text-slate-500">{l.lineNo}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-medium">{l.description}</div>
                          {l.board && <div className="text-[10px] text-slate-400 font-mono">{l.board.sku}</div>}
                          {l.item && <div className="text-[10px] text-slate-400 font-mono">{l.item.sku || l.item.code}</div>}
                        </td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">{qtyText(l.qty)} {l.uom}</td>
                        <td className="px-2 py-1.5 text-right">{money(l.rate)}</td>
                        <td className="px-2 py-1.5 text-right">{money(l.amount)}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">{l.gstPercent}% · {money(l.gstAmount)}</td>
                        <td className="px-2 py-1.5 text-right font-semibold">{money(l.total)}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">
                          {l.board ? (
                            l.uom === 'SHEETS'
                              ? <span className={l.receivedSheets >= l.qty ? 'text-emerald-700 font-semibold' : ''}>{qtyText(l.receivedSheets)} / {qtyText(l.qty)}</span>
                              : `${qtyText(l.receivedSheets)} sheets`
                          ) : l.item ? (
                            inItemUnit(l)
                              ? <span className={l.receivedQty >= l.qty ? 'text-emerald-700 font-semibold' : ''}>{qtyText(l.receivedQty)} / {qtyText(l.qty)}</span>
                              : `${qtyText(l.receivedQty)} ${l.item.uom}`
                          ) : (
                            <span className="text-slate-300" title={`Not tied to ${po.kind === 'BOARD' ? 'a board' : 'an item'}, so receipts can't be matched`}>—</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {receive && (
                            <button className="text-emerald-700 hover:underline font-medium whitespace-nowrap" onClick={receive}>
                              + Receive
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-3 text-xs">
              <div className="md:col-span-2 text-slate-600">
                <span className="text-slate-500">Amount in words: </span>
                <i>{amountInWords(po.grandTotal)}</i>
              </div>
              <div className="space-y-0.5">
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>₹{money(po.subTotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GST</span><span>₹{money(po.gstTotal)}</span></div>
                <div className="flex justify-between font-semibold text-sm border-t border-slate-200 pt-1"><span>Total</span><span>₹{money(po.grandTotal)}</span></div>
              </div>
            </div>

            {err && <div className="mt-3 text-sm text-red-700">{err}</div>}

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button className="btn-primary" onClick={() => downloadPdf()} disabled={pdfBusy}>{pdfBusy ? 'Building…' : 'Download PDF'}</button>
              <button className="btn-ghost" onClick={() => print()}>Print</button>
              {vendorForm && <>
                <span className="mx-1 h-5 w-px bg-slate-200" />
                <button className="btn-ghost border border-brand-200 text-brand" onClick={() => downloadPdf(vendorForm)} disabled={pdfBusy}
                        title={`The PO on ${po.vendorName}'s own order form`}>{vendorForm.label} PDF</button>
                <button className="btn-ghost border border-brand-200 text-brand" onClick={() => print(vendorForm)}>Print {vendorForm.label}</button>
              </>}
              {canEdit && <button className="btn-ghost" onClick={() => onEdit(po)}>Edit</button>}
              {canManage && <div className="ml-auto flex gap-2">
                {isOpen && (
                  <button
                    className="btn-ghost text-red-600"
                    disabled={closePo.isPending}
                    onClick={() => {
                      const msg = po.status === 'OPEN'
                        ? `Cancel ${po.poNumber}? Nothing has been received against it.`
                        : `Close ${po.poNumber}? It stops showing as pending; what was received stays in stock.`;
                      if (window.confirm(msg)) { setErr(null); closePo.mutate(); }
                    }}
                  >{po.status === 'OPEN' ? 'Cancel PO' : 'Close PO'}</button>
                )}
                {!isOpen && (
                  <button className="btn-ghost" disabled={reopenPo.isPending} onClick={() => { setErr(null); reopenPo.mutate(); }}>
                    Reopen
                  </button>
                )}
              </div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── editor ─────────────────────────── */

interface LineDraft {
  key: number;
  board: BoardRef | null;
  item: StoreItemRef | null;
  description: string;
  /** Description still the one filled from the board / item — replace it when that changes. */
  descFromPick: boolean;
  qty: string;
  uom: string;
  rate: string;
  /** Rate filled from the board / item, not typed — it follows the pick (and a board's unit). */
  rateFromPick: boolean;
  gstPercent: string;
  /** The supplier form's own columns for this line. */
  formFields: Record<string, string>;
}

let lineKey = 0;
const blankLine = (uom: string): LineDraft => ({
  key: ++lineKey, board: null, item: null, description: '', descFromPick: false,
  qty: '', uom, rate: '', rateFromPick: false, gstPercent: '18', formFields: {},
});

interface PaperStockRow {
  id: number; sku: string; paperType: string;
  lengthIn: number | null; widthIn: number | null; gsm: number | null;
  brand: string; rate: number | null; sheetsOnHand: number; isActive: boolean;
}

interface InventoryItemRow {
  itemCode: string; sku?: string | null; itemName: string; category: string; uom: string | null;
  itemGroup?: string | null; brand?: string | null;
  binLocation: string | null; standardCost: number | null; isActive: boolean; qtyOnHand?: number;
}

const toItemRef = (r: InventoryItemRow): StoreItemRef => ({
  code: r.itemCode, sku: r.sku ?? null, name: r.itemName, group: r.itemGroup ?? null, brand: r.brand ?? null,
  uom: r.uom, category: r.category,
  standardCost: r.standardCost == null ? null : Number(r.standardCost),
  qtyOnHand: Number(r.qtyOnHand) || 0, location: r.binLocation,
});

function PoEditor({ kind, scope, po, onClose, onSaved }: {
  kind: PoKind;
  scope?: StoreScope;
  po: Po | null;
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const qc = useQueryClient();
  const invalidate = usePoInvalidate();
  const isNew = !po;
  const isBoard = kind === 'BOARD';
  const defaultUom = isBoard ? 'SHEETS' : (scope?.uoms[0] ?? 'NOS');

  const vendors = useVendors();
  // A vendor added in Vendor Master a minute ago should be in the list.
  useEffect(() => { qc.invalidateQueries({ queryKey: ['lookup', 'vendors'] }); }, [qc]);

  const boardsQ = useQuery({
    enabled: isBoard,
    queryKey: ['paper-stock', { forPo: true }],
    queryFn: () => api<{ rows: PaperStockRow[] }>('/api/paper-stock?sort=sku'),
  });
  // Cutting stock (CUT01 ...) is never bought: not offered on a PO.
  const boards: BoardRef[] = useMemo(() => (boardsQ.data?.rows ?? []).filter((r) => !isCutSku(r.sku)).map((r) => ({
    id: r.id, sku: r.sku, paperType: r.paperType, lengthIn: r.lengthIn, widthIn: r.widthIn,
    gsm: r.gsm, brand: r.brand, rate: r.rate, sheetsOnHand: r.sheetsOnHand,
  })), [boardsQ.data]);

  const itemsQ = useQuery({
    enabled: !isBoard,
    queryKey: ['inv-items'],
    queryFn: () => api<{ rows: InventoryItemRow[] }>('/api/inventory/items'),
  });
  // This page's active items (raw material or spare parts).
  const items: StoreItemRef[] = useMemo(() => (itemsQ.data?.rows ?? [])
    .filter((r) => r.isActive && (scope?.categories ?? []).includes(r.category))
    .map(toItemRef), [itemsQ.data, scope]);

  const [vendorId, setVendorId] = useState(po?.vendorId ?? '');
  const [poNumber, setPoNumber] = useState(po?.poNumber ?? '');
  const [poDate, setPoDate] = useState(po?.poDate ?? todayIso());
  const [deliveryLocation, setDeliveryLocation] = useState(po?.deliveryLocation || BOARD_PO_DEFAULT_DELIVERY);
  const [terms, setTerms] = useState(po?.terms || BOARD_PO_DEFAULT_TERMS);
  const [remarks, setRemarks] = useState(po?.remarks ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() => po
    ? po.lines.map((l) => ({
        key: ++lineKey, board: l.board, item: l.item, description: l.description, descFromPick: false,
        qty: String(l.qty), uom: l.uom, rate: l.rate ? String(l.rate) : '', rateFromPick: false,
        gstPercent: String(l.gstPercent), formFields: l.formFields ?? {},
      }))
    : [blankLine(defaultUom)]);
  const [err, setErr] = useState<string | null>(null);

  const vendor = useQuery({
    enabled: !!vendorId,
    queryKey: ['board-po-vendor', vendorId],
    queryFn: () => api<{ id: string; name: string; address: string }>(`/api/board-po/vendor/${encodeURIComponent(vendorId)}`),
  });
  // The supplier's own order form (Century ...): its extra columns show on each line.
  const supplierForm = vendorFormatForName(vendor.data?.name ?? po?.vendorName, kind);

  const next = useQuery({
    enabled: isNew,
    queryKey: ['board-po-next', poDate, kind],
    queryFn: () => api<{ preview: string }>(`/api/board-po/next-number?date=${poDate}&kind=${kind}`),
  });

  const setLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  function pickBoard(key: number, b: BoardRef | null) {
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const next: LineDraft = { ...l, board: b };
      // Fill the description from the board unless the user wrote their own.
      if (b && (!l.description.trim() || l.descFromPick)) {
        next.description = boardSpec(b) || b.sku;
        next.descFromPick = true;
      }
      // The board's rate (per kg), in this line's unit — a starting point only.
      if (b && (!l.rate.trim() || l.rateFromPick)) {
        const r = boardRateIn(l.uom, b);
        next.rate = r != null ? String(r) : '';
        next.rateFromPick = r != null;
      }
      return next;
    }));
  }

  function pickItem(key: number, it: StoreItemRef | null) {
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const next: LineDraft = { ...l, item: it };
      if (it && (!l.description.trim() || l.descFromPick)) {
        // The maker goes on the PO with the name: the supplier quotes per brand.
        next.description = it.brand ? `${it.name} (${it.brand})` : it.name;
        next.descFromPick = true;
      }
      // Ordered in the item's own unit, so what arrives can be counted against it.
      if (it?.uom) next.uom = it.uom.toUpperCase();
      // The item's standard cost — a starting point only.
      if (it && (!l.rate.trim() || l.rateFromPick)) {
        const r = it.standardCost != null && it.standardCost > 0 ? it.standardCost : null;
        next.rate = r != null ? String(r) : '';
        next.rateFromPick = r != null;
      }
      return next;
    }));
  }

  function setUom(key: number, uom: string) {
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const next: LineDraft = { ...l, uom };
      // A rate taken from the board follows the unit; a typed one is left alone.
      if (l.board && (l.rateFromPick || !l.rate.trim())) {
        const r = boardRateIn(uom, l.board);
        next.rate = r != null ? String(r) : '';
        next.rateFromPick = r != null;
      }
      return next;
    }));
  }

  const parsed = lines.map((l) => ({
    ...l,
    qtyN: Number(l.qty) || 0,
    rateN: Number(l.rate) || 0,
    gstN: l.gstPercent.trim() === '' ? 0 : Number(l.gstPercent),
  }));
  const totals = parsed.map((l) => lineAmounts({ qty: l.qtyN, rate: l.rateN, gstPercent: l.gstN }));
  const grand = totals.reduce((s, t) => s + t.total, 0);

  const save = useMutation({
    mutationFn: (body: object) => api<{ id: number; poNumber: string }>(
      isNew ? '/api/board-po' : `/api/board-po/${po!.id}`,
      { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(body) },
    ),
    onSuccess: (r) => { invalidate(); onSaved(r.id); },
    onError: (e) => setErr(poError((e as Error).message)),
  });

  function submit() {
    setErr(null);
    if (!vendorId) { setErr('Pick the supplier (from Vendor Master).'); return; }
    if (!poDate) { setErr('PO date is required.'); return; }
    const used = parsed.filter((l) => l.board || l.item || l.description.trim() || l.qty.trim() || l.rate.trim());
    if (!used.length) { setErr('Add at least one line.'); return; }
    const seen = new Set<string>();
    for (const [i, l] of used.entries()) {
      const n = i + 1;
      if (!l.description.trim()) { setErr(`Line ${n}: enter a description.`); return; }
      if (!(l.qtyN > 0)) { setErr(`Line ${n}: enter the quantity.`); return; }
      if (l.rate.trim() && !(Number(l.rate) >= 0)) { setErr(`Line ${n}: rate must be a number.`); return; }
      if (!(l.gstN >= 0 && l.gstN <= 100)) { setErr(`Line ${n}: GST % must be between 0 and 100.`); return; }
      const picked = l.board ? { key: `b${l.board.id}`, name: l.board.sku } : l.item ? { key: `i${l.item.code.toUpperCase()}`, name: l.item.name } : null;
      if (picked) {
        if (seen.has(picked.key)) { setErr(`Line ${n}: ${picked.name} is already on another line.`); return; }
        seen.add(picked.key);
      }
    }
    save.mutate({
      ...(isNew ? { kind } : {}),
      poNumber: poNumber.trim() || null,
      poDate,
      vendorId,
      deliveryLocation: deliveryLocation.trim() || null,
      terms: terms.trim() || null,
      remarks: remarks.trim() || null,
      lines: used.map((l) => ({
        paperStockId: l.board?.id ?? null,
        itemCode: l.item?.code ?? null,
        description: l.description.trim(),
        qty: l.qtyN,
        uom: l.uom,
        rate: l.rateN,
        gstPercent: l.gstN,
        formFields: supplierForm ? l.formFields : null,
      })),
    });
  }

  const vendorOptions = vendors.data ?? [];
  const vendorMissing = !!vendorId && !vendors.isLoading && !vendorOptions.some((v) => v.id === vendorId);
  /** Units for a line not tied to an item (and the line's own, if it's something else). */
  const unitsFor = (l: LineDraft): string[] => isBoard
    ? [...BOARD_PO_UOMS]
    : [...new Set([...(scope?.uoms ?? []), 'NOS', l.uom].map((u) => u.toUpperCase()))];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-6xl w-full mx-4 max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-brand mb-4">
          {isNew ? `New ${KIND_TEXT[kind].title}` : `Edit ${po!.poNumber}`}
        </h2>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="label">Supplier *</label>
            <select className="input" value={vendorId} onChange={(e) => setVendorId(e.target.value)} disabled={vendors.isLoading}>
              <option value="">{vendors.isLoading ? 'Loading vendors…' : '— Select from Vendor Master —'}</option>
              {vendorOptions.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              {vendorMissing && <option value={vendorId}>{po?.vendorName ?? vendorId} (not in Vendor Master)</option>}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">
              Not listed? <Link to="/vendors/new" target="_blank" className="text-brand hover:underline">Add it to Vendor Master</Link>, then pick it here.
            </div>
          </div>
          <div>
            <label className="label">PO No.</label>
            <input
              className="input font-mono"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder={isNew ? (next.data?.preview ?? 'Auto') : ''}
            />
            {isNew && (
              <div className="text-[11px] text-slate-500 mt-1">
                Leave blank for {next.data?.preview ?? 'the next number'}. Type one to carry on your own series.
              </div>
            )}
          </div>
          <div>
            <label className="label">PO Date *</label>
            <input type="date" className="input" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="label">Supplier name &amp; address <span className="font-normal text-slate-400">· from Vendor Master</span></label>
            <div className="min-h-[64px] rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              {!vendorId ? <span className="text-slate-400">Pick a supplier to see what the PO will print.</span>
                : vendor.isLoading ? <span className="text-slate-400">Loading…</span>
                : vendor.data ? (
                  <>
                    <div className="font-semibold text-sm">{vendor.data.name}</div>
                    <div className="whitespace-pre-line text-slate-600">
                      {vendor.data.address || <span className="text-amber-700">No address in Vendor Master yet.</span>}
                    </div>
                  </>
                ) : <span className="text-red-600">This supplier is not in Vendor Master any more.</span>}
            </div>
            {vendorId && (
              <div className="text-[11px] text-slate-500 mt-1">
                Wrong or missing?{' '}
                <Link to={`/vendors/${encodeURIComponent(vendorId)}/edit`} target="_blank" className="text-brand hover:underline">
                  Fix it in Vendor Master
                </Link>{' '}— the PO always prints the current details.
              </div>
            )}
          </div>
          <div>
            <label className="label">Delivery location</label>
            <input className="input" value={deliveryLocation} onChange={(e) => setDeliveryLocation(e.target.value)} />
          </div>
        </div>

        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-700">Items</h3>
          <span className="text-[11px] text-slate-500">
            {isBoard
              ? 'Pick a board to fill the description in inches, or type any item.'
              : 'Pick an item from the item master (so receipts count against this PO), or type any item.'}
          </span>
        </div>
        <div className="border border-slate-200 rounded">
          <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
            <div className="col-span-3">{isBoard ? 'Board (stock SKU)' : 'Item (item master)'}</div>
            <div className="col-span-3">Description (prints on PO) *</div>
            <div className="col-span-1 text-right">Qty *</div>
            <div className="col-span-1">UOM</div>
            <div className="col-span-1 text-right">Rate ₹</div>
            <div className="col-span-1 text-right">GST %</div>
            <div className="col-span-1 text-right">Total ₹</div>
            <div className="col-span-1"></div>
          </div>
          {parsed.map((l, i) => {
            const hint = isBoard ? weightHint(l.qtyN, l.uom, l.board) : '';
            // An item's line is in the item's own unit.
            const unitLocked = !!l.item?.uom;
            return (
              <div key={l.key} className="grid grid-cols-12 gap-2 px-2 py-2 border-t border-slate-100 first:border-t-0 items-start">
                <div className="col-span-3">
                  {isBoard
                    ? <BoardPicker boards={boards} loading={boardsQ.isLoading} value={l.board} onChange={(b) => pickBoard(l.key, b)} />
                    : <ItemPicker items={items} loading={itemsQ.isLoading} value={l.item} onChange={(it) => pickItem(l.key, it)} />}
                </div>
                <div className="col-span-3">
                  <input
                    className="input"
                    value={l.description}
                    placeholder={isBoard ? 'e.g. Grey Back 26.5 x 38.5 inch, 340 GSM' : 'e.g. Gold foil 640 mm x 120 m'}
                    onChange={(e) => setLine(l.key, { description: e.target.value, descFromPick: false })}
                  />
                  {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
                  {l.item && (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      On hand {qtyText(l.item.qtyOnHand)} {l.item.uom ?? ''}
                    </div>
                  )}
                </div>
                <input
                  type="number" min={0} step="any" className="input col-span-1 text-right"
                  value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })}
                />
                <select
                  className="input col-span-1 px-1 disabled:bg-slate-50"
                  value={l.uom}
                  disabled={unitLocked}
                  title={unitLocked ? "The item's unit, from the item master" : undefined}
                  onChange={(e) => setUom(l.key, e.target.value)}
                >
                  {unitsFor(l).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <input
                  type="number" min={0} step="any" className="input col-span-1 text-right"
                  value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value, rateFromPick: false })}
                  title={l.uom === 'SHEETS' ? 'Rate per sheet' : `Rate per ${l.uom}`}
                />
                <input
                  type="number" min={0} max={100} step="any" className="input col-span-1 text-right"
                  value={l.gstPercent} onChange={(e) => setLine(l.key, { gstPercent: e.target.value })}
                />
                <div className="col-span-1 text-right text-xs font-semibold pt-2">{money(totals[i]?.total ?? 0)}</div>
                <div className="col-span-1 text-center pt-1.5">
                  {lines.length > 1 && (
                    <button type="button" className="text-red-500 hover:text-red-700" title="Remove line"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>✕</button>
                  )}
                </div>
                {/* The supplier form's own columns for this line (Century: reel/sheet, grain, discount, remarks). */}
                {supplierForm && (
                  <div className="col-span-12 grid grid-cols-12 gap-2 rounded bg-brand-50/40 px-2 py-1.5">
                    <div className="col-span-12 text-[10px] font-semibold uppercase tracking-wide text-brand">{supplierForm.label}</div>
                    {supplierForm.lineFields.map((fd) => {
                      const v = l.formFields[fd.key] ?? '';
                      const set = (val: string) => setLine(l.key, { formFields: { ...l.formFields, [fd.key]: val } });
                      return (
                        <label key={fd.key} className="flex flex-col gap-0.5 text-[11px] text-slate-500" style={{ gridColumn: `span ${fd.span} / span ${fd.span}` }}>
                          {fd.label}
                          {fd.options
                            ? <select className="input py-1 text-sm" value={v || fd.options[0]} onChange={(e) => set(e.target.value)}>
                                {fd.options.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            : <input className="input py-1 text-sm" value={v} placeholder={fd.placeholder} maxLength={200} onChange={(e) => set(e.target.value)} />}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button type="button" className="text-brand text-sm hover:underline mt-2" onClick={() => setLines((ls) => [...ls, blankLine(defaultUom)])}>
          + Add line
        </button>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div className="md:col-span-2 grid gap-3">
            <div>
              <label className="label">Terms and conditions <span className="font-normal text-slate-400">· one per line</span></label>
              <textarea className="input min-h-[140px] text-xs" value={terms} onChange={(e) => setTerms(e.target.value)} />
            </div>
            <div>
              <label className="label">Remarks <span className="font-normal text-slate-400">· optional, prints on the PO</span></label>
              <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs self-start space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>₹{money(totals.reduce((s, t) => s + t.amount, 0))}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GST</span><span>₹{money(totals.reduce((s, t) => s + t.gstAmount, 0))}</span></div>
            <div className="flex justify-between font-semibold text-sm border-t border-slate-200 pt-1"><span>Total</span><span>₹{money(grand)}</span></div>
            <div className="text-[11px] text-slate-500 italic pt-1">{amountInWords(grand)}</div>
          </div>
        </div>

        {err && <div className="mt-3 text-sm text-red-700">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : isNew ? 'Create PO' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Search-as-you-type board picker.  Every word must match somewhere in the
 * SKU, paper type, size, GSM or brand — "grey 26.5 340" finds the Grey Back
 * 26.5 x 38.5 in 340 GSM board.
 */
function BoardPicker({ boards, loading, value, onChange }: {
  boards: BoardRef[];
  loading: boolean;
  value: BoardRef | null;
  onChange: (b: BoardRef | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');

  const matches = useMemo(() => {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean);
    return boards
      .filter((b) => {
        const hay = `${b.sku} ${b.paperType} ${b.lengthIn ?? ''} ${b.widthIn ?? ''} ${b.gsm ?? ''} ${b.brand}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      .slice(0, 50);
  }, [boards, text]);

  return (
    <div className="relative">
      <input
        className="input"
        value={open ? text : value ? `${value.sku} · ${boardSpec(value)}` : ''}
        placeholder={loading ? 'Loading boards…' : 'Search board (optional)'}
        onFocus={() => { setText(''); setOpen(true); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        title={value ? `${value.sku} · ${boardSpec(value)}` : undefined}
      />
      {open && (
        <ul className="absolute z-50 mt-1 max-h-64 w-[26rem] max-w-[80vw] overflow-auto rounded border border-slate-200 bg-white shadow-lg text-xs">
          <li
            className="cursor-pointer px-3 py-1.5 text-slate-500 hover:bg-slate-50 border-b border-slate-100"
            onMouseDown={() => { onChange(null); setOpen(false); }}
          >— No board — just type the description</li>
          {matches.map((b) => (
            <li
              key={b.id}
              className={`cursor-pointer px-3 py-1.5 hover:bg-brand-50 ${value?.id === b.id ? 'bg-brand-50' : ''}`}
              onMouseDown={() => { onChange(b); setOpen(false); }}
            >
              <span className="font-mono font-semibold text-brand">{b.sku}</span>
              <span> · {boardSpec(b) || '—'}</span>
              <span className="text-slate-400"> · {qtyText(b.sheetsOnHand)} sheets on hand</span>
            </li>
          ))}
          {!loading && matches.length === 0 && <li className="px-3 py-1.5 text-slate-400">No board matches “{text}”.</li>}
        </ul>
      )}
    </div>
  );
}

/**
 * Search-as-you-type item picker (raw material / spare parts).  Every word
 * must match somewhere in the item's code, name or category.
 */
function ItemPicker({ items, loading, value, onChange }: {
  items: StoreItemRef[];
  loading: boolean;
  value: StoreItemRef | null;
  onChange: (it: StoreItemRef | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');

  const matches = useMemo(() => {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean);
    return items
      .filter((it) => {
        const hay = `${it.sku ?? ''} ${it.code} ${it.name} ${it.brand ?? ''} ${it.group ?? ''} ${it.category}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      .slice(0, 50);
  }, [items, text]);

  const label = (it: StoreItemRef) => `${it.sku || it.code} · ${it.name}${it.brand ? ` · ${it.brand}` : ''}`;

  return (
    <div className="relative">
      <input
        className="input"
        value={open ? text : value ? label(value) : ''}
        placeholder={loading ? 'Loading items…' : 'Search item (optional)'}
        onFocus={() => { setText(''); setOpen(true); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        title={value ? label(value) : undefined}
      />
      {open && (
        <ul className="absolute z-50 mt-1 max-h-64 w-[26rem] max-w-[80vw] overflow-auto rounded border border-slate-200 bg-white shadow-lg text-xs">
          <li
            className="cursor-pointer px-3 py-1.5 text-slate-500 hover:bg-slate-50 border-b border-slate-100"
            onMouseDown={() => { onChange(null); setOpen(false); }}
          >— No item — just type the description</li>
          {matches.map((it) => (
            <li
              key={it.code}
              className={`cursor-pointer px-3 py-1.5 hover:bg-brand-50 ${value?.code === it.code ? 'bg-brand-50' : ''}`}
              onMouseDown={() => { onChange(it); setOpen(false); }}
            >
              <span className="font-mono font-semibold text-brand">{it.sku || it.code}</span>
              <span> · {it.name}</span>
              {it.brand && <span className="text-slate-500"> · {it.brand}</span>}
              <span className="text-slate-400"> · {qtyText(it.qtyOnHand)} {it.uom ?? ''} on hand</span>
            </li>
          ))}
          {!loading && matches.length === 0 && (
            <li className="px-3 py-1.5 text-slate-400">
              {items.length ? `No item matches “${text}”.` : 'No items yet — add them with + Add Item on the Stock tab.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
